'use client'

import { useCallback, useMemo } from 'react'

import { useDeviceDetectorContext } from '../../../device-detector'
import { gtmDataLayerPush } from '../../../gtm'

import { type RxPdpAppPlacement } from './rxPdpAppRedemptionLinks'
import { useProductAppRedemptionEligibility } from './useProductAppRedemptionEligibility'

type RxPdpAppRedemptionAction =
  | 'app_cta_click'
  | 'app_cta_impression'
  | 'drawer_open'
  | 'paper_continue_click'
  | 'paper_cta_click'
  | 'qr_impression'
  | 'store_badges_click'

type Track = (event: {
  action: RxPdpAppRedemptionAction
  placement: RxPdpAppPlacement
}) => void

/** Tracks Rx app redemption interactions in GTM independently of experiment decisions. */
export const useRxPdpAppRedemptionTracking = ():
  { track: Track } | undefined => {
  const { deviceType } = useDeviceDetectorContext()
  const isEligible = useProductAppRedemptionEligibility()
  const track = useCallback<Track>(
    ({ action, placement }) => {
      gtmDataLayerPush({
        action,
        device: deviceType,
        event: 'prescription_redemption',
        placement,
        surface: 'rx_pdp',
      })
    },
    [deviceType],
  )

  return useMemo(
    () => (isEligible ? { track } : undefined),
    [isEligible, track],
  )
}
